import React from "react";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";

export function KpiCard({ icon: Icon, label, value, hint, accent = "primary", testid }) {
  return (
    <Card className="p-4 shadow-card border-border" data-testid={testid}>
      <div className="flex items-start justify-between">
        <div>
          <div className="text-xs text-muted-foreground uppercase tracking-wide font-medium">{label}</div>
          <div className="mt-1.5 text-2xl md:text-3xl mono font-semibold tabular">{value}</div>
          {hint && <div className="mt-1 text-xs text-muted-foreground">{hint}</div>}
        </div>
        {Icon && (
          <div className={`h-9 w-9 rounded-md flex items-center justify-center ${accent === "accent" ? "bg-accent/10 text-accent" : "bg-primary/10 text-primary"}`}>
            <Icon className="h-4 w-4" />
          </div>
        )}
      </div>
    </Card>
  );
}

export function RatingBadge({ rating }) {
  if (!rating) return null;
  const n = parseInt(String(rating).replace("SB", ""), 10);
  let cls = "bg-muted text-muted-foreground";
  let label = "Unrated";
  if (n <= 2) { cls = "bg-success/12 text-success border-success/30"; label = "Excellent"; }
  else if (n <= 5) { cls = "bg-info/12 text-info border-info/30"; label = "Acceptable"; }
  else if (n <= 7) { cls = "bg-warning/12 text-warning border-warning/30"; label = "Watch"; }
  else { cls = "bg-destructive/12 text-destructive border-destructive/30"; label = "High Risk"; }
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full border text-xs font-semibold ${cls}`} data-testid="rating-badge" title={`${rating}: ${label}`}>
      <span className="mono">{rating}</span>
      <span className="opacity-80">·</span>
      <span>{label}</span>
    </span>
  );
}

export function RiskFlagBadge({ sev, msg }) {
  const cls = sev === "HIGH" ? "bg-destructive/10 text-destructive border-destructive/30" : (sev === "MEDIUM" ? "bg-warning/10 text-warning border-warning/30" : "bg-success/10 text-success border-success/30");
  return (
    <div className={`px-3 py-2 rounded-md border ${cls} text-xs flex items-start gap-2`}>
      <Badge variant="outline" className={`text-[10px] ${cls} border-none px-1.5 py-0.5`}>{sev}</Badge>
      <span className="flex-1">{msg}</span>
    </div>
  );
}

export function StagePill({ stage }) {
  const map = {
    DRAFT: "bg-muted text-muted-foreground",
    KYC: "bg-info/10 text-info border-info/20",
    FIELD_VERIFICATION: "bg-info/10 text-info border-info/20",
    FINANCIALS: "bg-info/10 text-info border-info/20",
    CMA: "bg-warning/10 text-warning border-warning/20",
    CRA: "bg-warning/10 text-warning border-warning/20",
    ASSESSMENT: "bg-warning/10 text-warning border-warning/20",
    SECURITY: "bg-warning/10 text-warning border-warning/20",
    APPROVAL: "bg-accent/10 text-accent border-accent/20",
    SANCTIONED: "bg-success/10 text-success border-success/30",
    REJECTED: "bg-destructive/10 text-destructive border-destructive/30",
  };
  const cls = map[stage] || "bg-muted text-muted-foreground";
  return <span className={`inline-flex items-center px-2 py-0.5 rounded-md text-[11px] font-medium border border-transparent ${cls}`}>{stage?.replace(/_/g, " ")}</span>;
}

export function SectionHeader({ title, subtitle, action, testid }) {
  return (
    <div className="flex items-end justify-between gap-3 mb-3" data-testid={testid}>
      <div>
        <h2 className="text-base md:text-lg font-semibold heading">{title}</h2>
        {subtitle && <p className="text-xs md:text-sm text-muted-foreground mt-0.5">{subtitle}</p>}
      </div>
      {action}
    </div>
  );
}
